import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { UPDATE_NOTE } from '../graphql/mutations';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Define LocationPreference enum
const LocationPreference = {
  KIAWAH: 'KIAWAH',
  ISLE_OF_PALMS: 'ISLE_OF_PALMS',
  ISLE_OF_PALMS_WILD_DUNES: 'ISLE_OF_PALMS_WILD_DUNES',
  SEABROOK_ISLAND: 'SEABROOK_ISLAND'
};

// Define grouped area combinations
const AREA_GROUPS = {
  KIAWAH_SEABROOK: {
    value: 'KIAWAH_SEABROOK',
    label: 'Kiawah & Seabrook',
    areas: [LocationPreference.KIAWAH, LocationPreference.SEABROOK_ISLAND],
    isGroup: true
  },
  IOP_WILD_DUNES: {
    value: 'IOP_WILD_DUNES', 
    label: 'Isle of Palms & Wild Dunes',
    areas: [LocationPreference.ISLE_OF_PALMS, LocationPreference.ISLE_OF_PALMS_WILD_DUNES],
    isGroup: true
  }
};

// Available grouped areas for selection
const AVAILABLE_AREA_GROUPS = [
  AREA_GROUPS.KIAWAH_SEABROOK,
  AREA_GROUPS.IOP_WILD_DUNES
];

const EditNoteModal = ({ note, onClose, onSuccess, availableRoles, roleLabels, areaGroups }) => {
  const [title, setTitle] = useState(note?.title || '');
  const [body, setBody] = useState(note?.body || '');
  const [startDate, setStartDate] = useState(note?.startDate ? new Date(note.startDate).toISOString().split('T')[0] : '');
  const [endDate, setEndDate] = useState(note?.endDate ? new Date(note.endDate).toISOString().split('T')[0] : '');
  const [selectedRoles, setSelectedRoles] = useState({});
  const [selectedAreaGroup, setSelectedAreaGroup] = useState(''); // Changed to single selection
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [updateNote, { loading: updateNoteLoading }] = useMutation(UPDATE_NOTE, {
    onCompleted: (data) => {
      console.log('Note updated successfully:', data.updateNote);
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(data.updateNote);
      }
    },
    onError: (error) => {
      console.error('Error in updateNote mutation:', error);
      setError(error.message || 'Failed to update note');
      setIsSubmitting(false);
    }
  });

  // Initialize with note's current values
  useEffect(() => {
    if (note) {
      const initialRoles = availableRoles.reduce((acc, role) => {
        if (role !== 'ADMIN') {
          acc[role] = note.visibleToRoles?.includes(role) || false;
        }
        return acc;
      }, {});
      setSelectedRoles(initialRoles);

      // Initialize area group based on note's current values
      let matchedGroup = '';
      if (note.visibleToAreas && note.visibleToAreas.length > 0) {
        // Check which group matches the note's areas
        for (const [groupKey, groupData] of Object.entries(AREA_GROUPS)) {
          if (groupData.areas.length === note.visibleToAreas.length &&
              groupData.areas.every(area => note.visibleToAreas.includes(area))) {
            matchedGroup = groupKey;
            break;
          }
        }
      }
      setSelectedAreaGroup(matchedGroup);
    }
  }, [note, availableRoles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      setError('');
      
      if (!title.trim()) {
        throw new Error('Title is required');
      }
      
      if (!body.trim()) {
        throw new Error('Note content is required');
      }
      
      // Validate dates if both are provided
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        throw new Error('Start date must be before end date');
      }
      
      // Get selected roles
      const visibleToRoles = Object.keys(selectedRoles)
        .filter(role => selectedRoles[role] && role !== 'ADMIN');
      
      if (visibleToRoles.length === 0) {
        throw new Error('Select at least one role that can view this note');
      }

      // Get selected areas from the selected group
      let visibleToAreas = null;
      if (selectedAreaGroup) {
        const selectedGroup = AREA_GROUPS[selectedAreaGroup];
        if (selectedGroup) {
          visibleToAreas = selectedGroup.areas;
        }
      }
      
      console.log('Updating note with roles:', visibleToRoles);
      console.log('Updating note with areas:', visibleToAreas);
      
      await updateNote({
        variables: {
          id: note.id,
          input: {
            title,
            body,
            startDate: startDate || null,
            endDate: endDate || null,
            visibleToRoles,
            visibleToAreas: visibleToAreas
          }
        }
      });
      
    } catch (err) {
      console.error('Error updating note:', err);
      setError(err.message || 'Failed to update note');
      setIsSubmitting(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet',
    'link'
  ];

  const handleRoleToggle = (role) => {
    setSelectedRoles({
      ...selectedRoles,
      [role]: !selectedRoles[role]
    });
  };

  const handleAreaToggle = (area) => {
    setSelectedAreaGroup(area);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl overflow-y-auto max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Edit Note</h2>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close modal"
            tabIndex="0"
            onKeyDown={(e) => e.key === 'Enter' && onClose()}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Note title"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-1">
              Note Content *
            </label>
            <div className="border border-gray-300 rounded-md overflow-hidden">
              <ReactQuill
                theme="snow"
                value={body}
                onChange={setBody}
                modules={modules}
                formats={formats}
                placeholder="Write your note content here..."
                style={{ minHeight: '200px' }}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                Start Date (Optional)
              </label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
              />
            </div>
            
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                End Date (Optional)
              </label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-sky-500 focus:border-sky-500"
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visible to Roles * (Admins can always see notes)
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {availableRoles
                .filter(role => role !== 'ADMIN') // Don't show ADMIN option since they see everything
                .map(role => (
                <label key={role} className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={selectedRoles[role] || false}
                    onChange={() => handleRoleToggle(role)}
                    className="rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="text-sm text-gray-700">
                    {roleLabels[role] || role}
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Visible to Areas (Optional)
            </label>
            <div className="space-y-2">
              {/* Option for no area restriction */}
              <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50">
                <input
                  type="radio"
                  name="areaGroup"
                  value=""
                  checked={selectedAreaGroup === ''}
                  onChange={(e) => handleAreaToggle(e.target.value)}
                  className="text-sky-600 focus:ring-sky-500"
                />
                <span className="text-sm text-gray-700">
                  All Areas (no restriction)
                </span>
              </label>
              
              {/* Area group options */}
              {AVAILABLE_AREA_GROUPS.map(areaGroup => (
                <label key={areaGroup.value} className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50">
                  <input
                    type="radio"
                    name="areaGroup"
                    value={areaGroup.value}
                    checked={selectedAreaGroup === areaGroup.value}
                    onChange={(e) => handleAreaToggle(e.target.value)}
                    className="text-sky-600 focus:ring-sky-500"
                  />
                  <span className="text-sm text-gray-700">
                    {areaGroup.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || updateNoteLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-sky-600 border border-transparent rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting || updateNoteLoading ? 'Updating...' : 'Update Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditNoteModal; 